import {
    createSlice,
    createAsyncThunk,
    createAction,
    PayloadAction
} from '@reduxjs/toolkit'
import connectApi from '../../api/connectApi'
import {
    constants,
    sendMessageToWorkerWithResponse,
    internetConnect,
    PRODUCT_CATEGORY
} from 'helpers'
import { groupBy, uniq, map } from 'lodash'
import {
    hasLocalDataExpired,
    updateLocalDataExpirationTime
} from '../../datastores/LocalDataExpiration'
import { updateClientsWithTransactions } from 'helpers/updateClientsWithTransactions'
import { ClientType, VillageClientSliceNames } from '../../storeTypes'
import { ErrorResponse } from 'types'

interface VillageClientItem {
    loading: boolean
    error: string
    clientList: ClientType[]
    previousClientList: ClientType[]
    selectedClient?: ClientType
    prevSelectedClient?: ClientType
}

type VillageClientsState = Record<string, VillageClientItem>

interface VillagePayload {
    villageId: string
}

interface RefreshPayload {
    villageId: string
    clients: ClientType[]
}

interface StorePayload {
    villages: { sf_id: string }[]
}

interface OpenVillagePageState {
    openVillagePageData?: {
        villageId: string
        filterOptions?: string[]
        clientTab: number
    }
}

interface FilterSelectorState {
    selectedFilter?: string
    openMoreFilters?: boolean
}

interface VillageSelectionState {
    url: string | ((villageId: string) => string)
}

interface VillagesClientCountState {
    totalClientObj: Record<
        string,
        { NbCurrentClients: number; NbPrevClients: number }
    >
}

interface VeClientListState {
    clientList: ClientType[]
    prevClientList: ClientType[]
}

const fetchFromIndexStore = async (villageId: string) => {
    const storedClientData = await sendMessageToWorkerWithResponse({
        [constants.MESSAGES.GET_CLIENTS]: {
            villageIds: [villageId],
            clientType: constants.CLIENT_TYPES.CURRENT_CLIENT,
            filter: 'village'
        }
    })
    const storedPrevClientData = await sendMessageToWorkerWithResponse({
        [constants.MESSAGES.GET_CLIENTS]: {
            villageIds: [villageId],
            clientType: constants.CLIENT_TYPES.PREVIOUS_CLIENT,
            filter: 'village'
        }
    })
    return {
        clientList: storedClientData.storedData,
        prevClientList: storedPrevClientData.storedData
    }
}

export async function fetchCategoriesForClients(clientCodes: string[]) {
    const uniqueCodes = uniq(clientCodes).filter(Boolean)

    const entries = await Promise.all(
        uniqueCodes.map(async (code) => {
            try {
                const response = await sendMessageToWorkerWithResponse({
                    [constants.MESSAGES.GET_PRODUCT_CATEGORIES]: code
                })
                return [code, response.categories]
            } catch {
                return [code, []]
            }
        })
    )
    return Object.fromEntries(entries)
}

function enrichClients(
    clients: ClientType[],
    categoriesByClient: Record<string, string[]>,
    villageId: string
): ClientType[] {
    const ORDER = Object.values(PRODUCT_CATEGORY)
    return clients.map((client) => {
        const categories = categoriesByClient[client.client_code] || []
        return {
            ...client,
            village_id: villageId,
            categories: categories
                .slice()
                .sort(
                    (a, b) =>
                        ORDER.indexOf(a.toLowerCase()) -
                        ORDER.indexOf(b.toLowerCase())
                )
        }
    })
}

export const refreshVillageClientListAction = createAsyncThunk(
    VillageClientSliceNames.REFRESH_VILLAGE_CLIENT_LIST,
    async ({ villageId, clients }: RefreshPayload, { rejectWithValue }) => {
        try {
            const clientsByVillageId = await sendMessageToWorkerWithResponse({
                'send client list data': { villageId, clients }
            })
            const { client, previous } = clientsByVillageId?.[villageId] ?? {}
            return {
                villageId,
                clientList: (client as ClientType[]) ?? [],
                previousClientList: (previous as ClientType[]) ?? []
            }
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            }
            return rejectWithValue('Unknown error')
        }
    }
)

export const failVillageClientList = createAction<{
    villageId: string
    error: string
}>('villageClients/failVillageClientList')

export const storeVillageClientListAction = createAsyncThunk(
    VillageClientSliceNames.STORE_VILLAGE_CLIENT_LIST,
    async ({ villages }: StorePayload, { rejectWithValue, dispatch }) => {
        try {
            if (!internetConnect()) return
            const { ALL_CLIENT_LIST } = constants.endpoints

            for (const village of villages) {
                const res = await connectApi.get(ALL_CLIENT_LIST, {
                    params: { village: village.sf_id }
                })

                if (res.status === 200) {
                    updateLocalDataExpirationTime(
                        constants.CLIENT_LIST_EXPIRATION_NAME,
                        village.sf_id,
                        constants.CLIENT_LIST_EXPIRATION_DURATION
                    )
                    const { transactions } =
                        await sendMessageToWorkerWithResponse({
                            'get transactions': { villageId: village.sf_id }
                        })
                    const refreshed = await updateClientsWithTransactions(
                        village.sf_id,
                        transactions,
                        dispatch,
                        res.data,
                        refreshVillageClientListAction
                    )
                    if (
                        !refreshed &&
                        Array.isArray(res.data) &&
                        res.data.length === 0
                    ) {
                        await dispatch(
                            refreshVillageClientListAction({
                                villageId: village.sf_id,
                                clients: []
                            })
                        )
                    }
                } else {
                    dispatch(
                        failVillageClientList({
                            villageId: village.sf_id,
                            error: 'Un problème est survenu'
                        })
                    )
                }
            }

            return { success: true }
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            }
            return rejectWithValue('Unknown error')
        }
    }
)

async function enrichVillageClientListAfterPaint({
    villageId,
    clientList,
    prevClientList,
    dispatch,
    refreshAfterExpiry
}: {
    villageId: string
    clientList: ClientType[]
    prevClientList: ClientType[]
    dispatch: (action: unknown) => unknown
    refreshAfterExpiry: boolean
}) {
    try {
        let currentClients = clientList
        let previousClients = prevClientList

        if (refreshAfterExpiry) {
            await dispatch(
                storeVillageClientListAction({
                    villages: [{ sf_id: villageId }]
                })
            )
            const refreshed = await fetchFromIndexStore(villageId)
            currentClients = refreshed.clientList
            previousClients = refreshed.prevClientList
        }

        const allCodes = map(currentClients, 'client_code')
        const categoriesByClient =
            allCodes.length > 0
                ? await fetchCategoriesForClients(allCodes)
                : {}
        const enrichedClients = enrichClients(
            currentClients,
            categoriesByClient,
            villageId
        )
        const enrichedPrevClients = enrichClients(
            previousClients,
            {},
            villageId
        )
        dispatch({
            type: 'villageClients/applyVillageClientLists',
            payload: {
                villageId,
                clientList: enrichedClients,
                previousClientList: enrichedPrevClients
            }
        })
    } catch (error: unknown) {
        console.error(error)
    }
}

export const villageClientListAction = createAsyncThunk(
    VillageClientSliceNames.FETCH_VILLAGE_CLIENT_LIST,
    async ({ villageId }: VillagePayload, { dispatch, rejectWithValue }) => {
        try {
            const isDataExpired = hasLocalDataExpired(
                constants.CLIENT_LIST_EXPIRATION_NAME,
                villageId
            )
            let { clientList, prevClientList } = await fetchFromIndexStore(
                villageId
            )
            const cacheEmpty =
                clientList.length === 0 && prevClientList.length === 0

            if (isDataExpired && cacheEmpty) {
                await dispatch(
                    storeVillageClientListAction({
                        villages: [{ sf_id: villageId }]
                    })
                )
                const refreshed = await fetchFromIndexStore(villageId)
                clientList = refreshed.clientList
                prevClientList = refreshed.prevClientList
            }

            dispatch(fetchingPreviousClient({ villageId }))

            dispatch(
                fetchPreviousClientList({
                    villageId,
                    list: prevClientList
                })
            )

            const immediateClients = enrichClients(clientList, {}, villageId)
            const immediatePrevClients = enrichClients(
                prevClientList,
                {},
                villageId
            )

            enrichVillageClientListAfterPaint({
                villageId,
                clientList,
                prevClientList,
                dispatch,
                refreshAfterExpiry: isDataExpired && !cacheEmpty
            }).catch((error: unknown) => {
                console.error(error)
            })

            return {
                villageId,
                clientListData: immediateClients,
                prevClientListData: immediatePrevClients
            }
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            }
            return rejectWithValue('Unknown error')
        }
    }
)

export const updateSingleClientBalAndSmsAction = createAsyncThunk(
    VillageClientSliceNames.UPDATE_SINGLE_CLIENT_BAL_AND_SMS,
    async (
        { villageId, clientId }: { villageId: string; clientId: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await connectApi.get(
                constants.endpoints.ALL_CLIENT_LIST,
                {
                    params: { client_codes: clientId }
                }
            )
            const transactionsResponse = await connectApi.get(
                constants.endpoints.TRANSACTIONS,
                {
                    params: { client_codes: clientId }
                }
            )
            if (response.data.length > 0) {
                const client = response.data[0]
                client.villageName = client.village
                client.village_id = villageId
                client._is_new = true
                client.smss = transactionsResponse.data
                await sendMessageToWorkerWithResponse({
                    'update balance and sms for client data': {
                        payload: client
                    }
                })
            }
            return { villageId, clientList: response.data as ClientType[] }
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            }
            return rejectWithValue('Unknown error')
        }
    }
)

export const getVeClientListAction = createAsyncThunk(
    VillageClientSliceNames.GET_VE_CLIENT_LIST,
    async (villages: { sf_id: string }[]) => {
        const mergedClientList: ClientType[] = []
        const mergedPrevClientList: ClientType[] = []
        await Promise.all(
            villages.map(async (village) => {
                const { clientList, prevClientList } =
                    await fetchFromIndexStore(village.sf_id)
                mergedClientList.push(...clientList)
                mergedPrevClientList.push(...prevClientList)
            })
        )
        return {
            clientList: mergedClientList,
            prevClientList: mergedPrevClientList
        }
    }
)

export const handleMultipleVillagesCountAction = createAsyncThunk(
    VillageClientSliceNames.HANDLE_MULTIPLE_VILLAGES_COUNT,
    async (villageIds: string[]) => {
        const storedClientData = await sendMessageToWorkerWithResponse({
            [constants.MESSAGES.GET_CLIENTS]: {
                filter: 'village',
                clientType: constants.CLIENT_TYPES.ALL_CLIENT,
                villageIds
            }
        })
        const clientsByVillage = groupBy(
            storedClientData.storedData as ClientType[],
            'village_id'
        )
        const totalClientObj: Record<
            string,
            { NbCurrentClients: number; NbPrevClients: number }
        > = {}
        for (const [key, value] of Object.entries(clientsByVillage)) {
            const [NbCurrentClients, NbPrevClients] = value.reduce(
                ([current, prev], client) =>
                    (client.all_enrollment ?? 0) > 0
                        ? [current + 1, prev]
                        : [current, prev + 1],
                [0, 0]
            )
            totalClientObj[key] = { NbCurrentClients, NbPrevClients }
        }
        return totalClientObj
    }
)

const initialState: VillageClientsState = {}

const getInitialVillageState = (): VillageClientItem => ({
    loading: false,
    error: '',
    clientList: [],
    previousClientList: []
})

const villageClientsSlice = createSlice({
    name: VillageClientSliceNames.VILLAGE_CLIENTS,
    initialState,
    reducers: {
        selectVillageClient: (
            state,
            action: PayloadAction<{ clientId: string; villageId: string }>
        ) => {
            const { clientId, villageId } = action.payload
            const client = state[villageId]?.clientList?.find(
                (c) => c.client_code === clientId
            )
            if (client) state[villageId].selectedClient = client
        },
        resetSelectVillageClient: (
            state,
            action: PayloadAction<{ villageId: string }>
        ) => {
            const { villageId } = action.payload
            if (state[villageId]) state[villageId].selectedClient = undefined
        },
        selectVillagePrevClient: (
            state,
            action: PayloadAction<{ clientId: string; villageId: string }>
        ) => {
            const { clientId, villageId } = action.payload
            const client = state[villageId]?.previousClientList?.find(
                (c) => c.client_code === clientId
            )
            if (client) state[villageId].prevSelectedClient = client
        },
        clearSingleClientData: (state) => {
            Object.keys(state).forEach((villageId) => {
                state[villageId].clientList = []
            })
        },
        applyVillageClientLists: (
            state,
            action: PayloadAction<{
                villageId: string
                clientList: ClientType[]
                previousClientList: ClientType[]
            }>
        ) => {
            const { villageId, clientList, previousClientList } = action.payload
            if (!state[villageId]) {
                state[villageId] = getInitialVillageState()
            }
            state[villageId].clientList = clientList
            state[villageId].previousClientList = previousClientList
        },
        selectedClientAction: (
            state,
            action: PayloadAction<{
                clientData: ClientType
                clientType: string
            }>
        ) => {
            const { clientData, clientType } = action.payload
            const villageId = clientData.village_id
            if (!villageId) return
            if (!state[villageId]) state[villageId] = getInitialVillageState()
            if (clientType === 'current') {
                state[villageId].selectedClient = clientData
            } else {
                state[villageId].prevSelectedClient = clientData
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(villageClientListAction.pending, (state, action) => {
                const villageId = action.meta.arg.villageId
                if (!state[villageId]) {
                    state[villageId] = getInitialVillageState()
                }
                const hasCachedList =
                    state[villageId].clientList.length > 0 ||
                    state[villageId].previousClientList.length > 0
                if (!hasCachedList) {
                    state[villageId].loading = true
                }
                state[villageId].error = ''
            })
            .addCase(villageClientListAction.fulfilled, (state, action) => {
                const { villageId, clientListData, prevClientListData } =
                    action.payload
                if (!state[villageId]) {
                    state[villageId] = getInitialVillageState()
                }
                state[villageId].loading = false
                state[villageId].clientList = clientListData
                state[villageId].previousClientList = prevClientListData
            })
            .addCase(villageClientListAction.rejected, (state, action) => {
                const villageId = action.meta.arg.villageId
                if (!state[villageId]) {
                    state[villageId] = getInitialVillageState()
                }
                state[villageId].loading = false
                state[villageId].error = action.payload as string
            })
            .addCase(failVillageClientList, (state, action) => {
                const { villageId, error } = action.payload
                if (!state[villageId]) {
                    state[villageId] = getInitialVillageState()
                }
                state[villageId].loading = false
                state[villageId].error = error
            })
            .addCase(
                refreshVillageClientListAction.fulfilled,
                (state, action) => {
                    const { villageId, clientList, previousClientList } =
                        action.payload
                    if (!state[villageId]) {
                        state[villageId] = getInitialVillageState()
                    }
                    state[villageId].loading = false
                    state[villageId].error = ''
                    state[villageId].clientList = clientList || []
                    state[villageId].previousClientList =
                        previousClientList || []
                }
            )
            .addCase(
                refreshVillageClientListAction.rejected,
                (state, action) => {
                    const villageId = action.meta.arg.villageId
                    if (state[villageId]) {
                        state[villageId].loading = false
                        state[villageId].error = action.error.message || ''
                    }
                }
            )
    }
})

const openVillagePageInitialState: OpenVillagePageState = {}

export const openVillagePageSlice = createSlice({
    name: VillageClientSliceNames.OPEN_VILLAGE_PAGE,
    initialState: openVillagePageInitialState,
    reducers: {
        openVillagePageAction: (
            _,
            action: PayloadAction<{
                villageId: string
                filterOptions?: string[]
                clientTab: number
            }>
        ) => ({
            openVillagePageData: {
                villageId: action.payload.villageId,
                filterOptions: action.payload.filterOptions,
                clientTab: action.payload.clientTab
            }
        })
    }
})

export const filterSelectorSlice = createSlice({
    name: VillageClientSliceNames.FILTER_SELECTOR,
    initialState: {} as FilterSelectorState, // NOSONAR: required so reducers can assign the optional fields; {} alone infers type {}
    reducers: {
        filterSelectorAction: (
            _,
            action: PayloadAction<{
                selectedFilter?: string
                openMoreFilters?: boolean
            }>
        ) => ({
            selectedFilter: action.payload.selectedFilter,
            openMoreFilters: action.payload.openMoreFilters
        })
    }
})

const villageSelectionInfoSlice = createSlice({
    name: VillageClientSliceNames.VILLAGE_SELECTION_INFO,
    initialState: { url: '' } as VillageSelectionState, // NOSONAR: required - without it, url narrows to the literal type 'string' only
    reducers: {
        setSelectionVillageUrl: (
            state,
            action: PayloadAction<{
                url: string | ((villageId: string) => string)
            }>
        ) => {
            state.url = action.payload.url
        },
        clearSelectionVillageInfo: (state) => {
            state.url = ''
        }
    }
})

export const veClientListSlice = createSlice({
    name: VillageClientSliceNames.VE_CLIENT_LIST,
    initialState: { clientList: [], prevClientList: [] } as VeClientListState, // NOSONAR: required - without it, [] narrows to never[]
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getVeClientListAction.fulfilled, (_, action) => ({
            clientList: action.payload.clientList,
            prevClientList: action.payload.prevClientList
        }))
    }
})

const multipleVillagesCountInitialState: VillagesClientCountState = {
    totalClientObj: {}
}

export const multipleVillagesCountSlice = createSlice({
    name: VillageClientSliceNames.MULTIPLE_VILLAGES_COUNT,
    initialState: multipleVillagesCountInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            handleMultipleVillagesCountAction.fulfilled,
            (_, action) => ({
                totalClientObj: action.payload
            })
        )
    }
})

interface VillagePreviousClientItem {
    loading: boolean
    error: string
    previousClientList: ClientType[]
    prevSelectedClient?: ClientType
}

type VillagePreviousClientState = Record<string, VillagePreviousClientItem>

const initialPreviousClientState: VillagePreviousClientState = {}

export const villagePreviousClientSlice = createSlice({
    name: VillageClientSliceNames.VILLAGE_PREVIOUS_CLIENTS,
    initialState: initialPreviousClientState,
    reducers: {
        fetchingPreviousClient: (
            state,
            action: PayloadAction<{ villageId: string }>
        ) => {
            const { villageId } = action.payload
            const existing = state[villageId] || {
                loading: false,
                error: '',
                previousClientList: [],
                prevSelectedClient: undefined
            }

            state[villageId] = {
                ...existing,
                loading: true,
                error: ''
            }
        },

        fetchPreviousClientList: (
            state,
            action: PayloadAction<{ villageId: string; list: ClientType[] }>
        ) => {
            const { villageId, list } = action.payload
            state[villageId] = {
                loading: false,
                error: '',
                previousClientList: list
            }
        },

        failPreviousClientRequest: (
            state,
            action: PayloadAction<{ villageId: string; error: string }>
        ) => {
            const { villageId, error } = action.payload
            const existing = state[villageId] || {
                loading: false,
                error: '',
                previousClientList: [],
                prevSelectedClient: undefined
            }

            state[villageId] = {
                ...existing,
                loading: false,
                error
            }
        },

        prevSelectVillageClient: (
            state,
            action: PayloadAction<{ villageId: string; clientId: string }>
        ) => {
            const { villageId, clientId } = action.payload
            const prevList = state[villageId]?.previousClientList || []
            const selected = prevList.find((c) => c.client_code === clientId)

            if (!state[villageId]) {
                state[villageId] = {
                    loading: false,
                    error: '',
                    previousClientList: [],
                    prevSelectedClient: undefined
                }
            }

            state[villageId].prevSelectedClient = selected
        },

        selectedPrevClient: (
            state,
            action: PayloadAction<{ villageId: string; client: ClientType }>
        ) => {
            const { villageId, client } = action.payload
            if (!state[villageId]) {
                state[villageId] = {
                    loading: false,
                    error: '',
                    previousClientList: [],
                    prevSelectedClient: undefined
                }
            }
            state[villageId].prevSelectedClient = client
        }
    }
})

export const {
    fetchingPreviousClient,
    fetchPreviousClientList,
    failPreviousClientRequest,
    prevSelectVillageClient,
    selectedPrevClient
} = villagePreviousClientSlice.actions

interface SingleClientState {
    loading: boolean
    error: string
    clientList: ClientType[]
}

const singleClientSlice = createSlice({
    name: 'singleClient',
    initialState: { // NOSONAR: required - without it, clientList: [] narrows to never[]
        loading: false,
        error: '',
        clientList: []
    } as SingleClientState,
    reducers: {
        fetchingSingleClient: (state) => {
            state.loading = true
            state.error = ''
            state.clientList = []
        },
        fetchedSingleClient: (state, action: PayloadAction<ClientType[]>) => {
            state.loading = false
            state.error = ''
            state.clientList = action.payload
        },
        failSingleClientRequest: (state, action: PayloadAction<string>) => {
            state.loading = false
            state.error = action.payload
            state.clientList = []
        },
        clearFetchedSingleClient: (state) => {
            state.loading = false
            state.error = ''
            state.clientList = []
        }
    }
})

export const {
    fetchingSingleClient,
    fetchedSingleClient,
    failSingleClientRequest,
    clearFetchedSingleClient
} = singleClientSlice.actions

export const singleClientReducer = singleClientSlice.reducer

export const {
    selectVillageClient,
    resetSelectVillageClient,
    selectVillagePrevClient,
    clearSingleClientData,
    applyVillageClientLists,
    selectedClientAction
} = villageClientsSlice.actions

export const { setSelectionVillageUrl, clearSelectionVillageInfo } =
    villageSelectionInfoSlice.actions
export const { openVillagePageAction } = openVillagePageSlice.actions
export const { filterSelectorAction } = filterSelectorSlice.actions

export const villageClientReducer = villageClientsSlice.reducer
export const villagePreviousClientReducer = villagePreviousClientSlice.reducer
export const openVillagePageReducer = openVillagePageSlice.reducer
export const filterSelectorReducer = filterSelectorSlice.reducer
export const villageSelectionInfoReducer = villageSelectionInfoSlice.reducer
export const veClientListReducer = veClientListSlice.reducer
export const multipleVillagesCountReducer = multipleVillagesCountSlice.reducer
